import { GlobalActions } from '@fcom/core/actions';
import { DataUtils, setIn } from '@fcom/core/utils';
import { GlobalBookingActions } from '@fcom/common/store';
import { SortBy } from '@fcom/common-booking';
import { defaultFilterValues } from '@fcom/common-booking/interfaces/flight-filters.interface';
import { leaveError } from '@fcom/common/store/actions/error.actions';
import { AirOffersState, AirOffersStatus } from '@fcom/common/interfaces/booking/air-offers-state.interface';

import { BookingActions, OffersActions } from '../actions';

export const initialState: AirOffersState = Object.seal({
  status: AirOffersStatus.INITIAL,
  currentOffers: undefined,
  selectedOutboundId: undefined,
  selectedOutboundFareFamilyCode: undefined,
  selectedInboundId: undefined,
  selectedInboundFareFamilyCode: undefined,
  sortBy: SortBy.DURATION,
  filters: {
    stopsCount: defaultFilterValues.defaultStopsOption,
    cabin: defaultFilterValues.defaultTravelClass,
    arrivalTime: [...defaultFilterValues.defaultTimeOptions],
    departureTime: [...defaultFilterValues.defaultTimeOptions],
  },
  multiCity: {
    offers: [],
    airlines: {},
    bounds: {},
    fareFamilies: {},
    hasFlightsInUnitedStates: null,
    locations: {},
    selectedFareFamily: null,
    messages: null,
    status: null,
    surcharges: [],
    selectedItinerary: [],
    hashMap: {},
  },
});

type OfferActionsUnionType =
  | OffersActions.ActionsUnion
  | GlobalActions.ActionsUnion
  | BookingActions.ActionsUnion
  | GlobalBookingActions.GlobalBookingActionsUnion
  | typeof leaveError;

// eslint-disable-next-line complexity
export function offersReducer(state = initialState, action: OfferActionsUnionType): AirOffersState {
  /* eslint-disable no-fallthrough */
  switch (action.type) {
    case OffersActions.startLoading.type:
      return DataUtils.wrap(initialState)
        .setIn('status', AirOffersStatus.PENDING)
        .setIn('sortBy', state.sortBy)
        .value();
    case OffersActions.success.type:
      return setIn(state, 'status', AirOffersStatus.OK);
    case OffersActions.error.type:
      return setIn(state, 'status', AirOffersStatus.ERROR);
    case OffersActions.invalidInput.type:
      return setIn(state, 'status', AirOffersStatus.INVALID_INPUT);
    case OffersActions.noFlightsFound.type:
      return setIn(state, 'status', AirOffersStatus.NO_FLIGHTS_FOUND);
    case OffersActions.setOffers.type:
      return DataUtils.wrap(state).setIn('currentOffers', action.offers).setIn('status', AirOffersStatus.OK).value();
    case OffersActions.offerSetLastRequestParams.type:
      return setIn(state, 'lastRequestParams', action.params);
    case OffersActions.setOutboundId.type:
      return setIn(state, 'selectedOutboundId', action.outboundId);
    case OffersActions.setOutboundFareFamily.type:
      return setIn(state, 'selectedOutboundFareFamilyCode', action.outboundFareFamily);
    case OffersActions.setInboundId.type:
      return setIn(state, 'selectedInboundId', action.inboundId);
    case OffersActions.setInboundFareFamily.type:
      return setIn(state, 'selectedInboundFareFamilyCode', action.inboundFareFamily);
    case OffersActions.setSortBy.type:
      return setIn(state, 'sortBy', action.sortBy);
    case OffersActions.setArrivalTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          arrivalTime: action.arrivalTime,
        },
      };

    case OffersActions.resetArrivalTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          arrivalTime: defaultFilterValues.defaultTimeOptions,
        },
      };

    case OffersActions.setDepartureTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          departureTime: action.departureTime,
        },
      };

    case OffersActions.resetDepartureTime.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          departureTime: defaultFilterValues.defaultTimeOptions,
        },
      };

    case OffersActions.setStopsCount.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          stopsCount: action.stopsCount,
        },
      };

    case OffersActions.resetStopsCount.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          stopsCount: defaultFilterValues.defaultStopsOption,
        },
      };

    case OffersActions.setCabin.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          cabin: action.cabin,
        },
      };

    case OffersActions.resetCabin.type:
      return {
        ...state,
        filters: {
          ...state.filters,
          cabin: defaultFilterValues.defaultTravelClass,
        },
      };

    case OffersActions.resetAllFilters.type: {
      return {
        ...state,
        filters: {
          stopsCount: defaultFilterValues.defaultStopsOption,
          cabin: action.keepCabin ? state.filters.cabin : defaultFilterValues.defaultTravelClass,
          arrivalTime: [...defaultFilterValues.defaultTimeOptions],
          departureTime: [...defaultFilterValues.defaultTimeOptions],
        },
      };
    }
    case leaveError.type:
    case OffersActions.reset.type:
    case BookingActions.setTravelType.type: // @todo do we need this ????
    case BookingActions.selectTravelClass.type: // @todo do we need this ????
    // Clean up offers when order created
    case GlobalActions.confirmation.type:
      return setIn({ ...initialState, lastRequestParams: state.lastRequestParams }, 'sortBy', state.sortBy);
    default:
      return state;
  }
  /* eslint-enable no-fallthrough */
}
